import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import articleApi from '../services/articleApi';
import { toast } from 'react-toastify';
import SimpleRichTextEditor from './SimpleRichTextEditor';

const ArticleManage = () => {
  const { user, isAuthenticated } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: '',
    is_published: true,
    image: null,
    order: 0,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [sortMode, setSortMode] = useState('order'); // 'order' 或 'time'
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const isOwner = (article) => {
    if (!user) return false;
    // 如果是管理員，可以管理所有文章
    if (user.role === 'ADMIN') return true;
    // 如果是教練，只能管理自己的文章
    if (user.role === 'COACH') return article?.author?.id === user.id;
    // 否則只有作者可以管理
    return article?.author?.id === user.id;
  };

  useEffect(() => {
    loadArticles();
  }, [searchTerm]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const data = await articleApi.getManageArticles();
      setArticles(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('載入文章失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        title: article.title,
        content: article.content,
        tags: article.tags,
        is_published: article.is_published,
        image: null,
        order: article.order || 0,
      });
      setPreviewImage(article.image || null);
    } else {
      setEditingArticle(null);
      setFormData({ 
        title: '', 
        content: '', 
        tags: '', 
        is_published: true, 
        image: null,
        order: 0 
      });
      setPreviewImage(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingArticle(null);
    setPreviewImage(null);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('Selected file:', file.name, file.type, file.size);
      setFormData((prev) => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onload = (e) => setPreviewImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('content', formData.content);
      submitData.append('tags', formData.tags);
      submitData.append('is_published', formData.is_published);
      submitData.append('order', formData.order);
      if (formData.image) {
        console.log('Adding image to FormData:', formData.image.name, formData.image.type, formData.image.size);
        submitData.append('image', formData.image);
      }
      
      console.log('FormData contents:');
      for (let [key, value] of submitData.entries()) {
        console.log(key, value);
      }

      if (editingArticle) {
        await articleApi.updateArticle(editingArticle.id, submitData);
        toast.success('文章更新成功');
      } else {
        await articleApi.createArticle(submitData);
        toast.success('文章發布成功');
      }
      handleCloseDialog();
      loadArticles();
    } catch (error) {
      console.error('Article operation error:', error);
      const errorMessage = error.response?.data?.detail || error.message || (editingArticle ? '更新文章失敗' : '發布文章失敗');
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('確定要刪除這篇文章嗎？')) return;
    try {
      await articleApi.deleteArticle(articleId);
      toast.success('文章刪除成功');
      loadArticles();
    } catch (error) {
      toast.error('刪除文章失敗');
    }
  };

  // 拖拽排序功能
  const handleDragStart = (e, index) => {
    setIsDragging(true);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    // 使用 sortedArticles 來確保拖拽基於當前顯示的順序
    const newArticles = [...sortedArticles];
    const draggedArticle = newArticles[draggedIndex];
    newArticles.splice(draggedIndex, 1);
    newArticles.splice(dropIndex, 0, draggedArticle);

    // 更新 order 值 - 注意：由於顯示是倒序，需要反向分配 order
    const updatedArticles = newArticles.map((article, index) => ({
      ...article,
      order: newArticles.length - index  // 反向分配：第一個顯示的文章 order 最大
    }));

    setArticles(updatedArticles);
    setIsDragging(false);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedIndex(null);
  };

  const saveOrder = async () => {
    try {
      // 使用 sortedArticles 來確保保存的是當前顯示的順序
      const articleOrders = sortedArticles.map((article, index) => ({
        id: article.id,
        order: sortedArticles.length - index  // 反向分配 order 值
      }));
      
      await articleApi.updateArticleOrder(articleOrders);
      toast.success('文章順序已保存');
    } catch (error) {
      console.error('保存順序失敗:', error);
      toast.error('保存順序失敗');
    }
  };

  const sortedArticles = useMemo(() => {
    if (sortMode === 'order') {
      // 在排序模式下，按 order 倒序排列（order 大的在前）
      return [...articles].sort((a, b) => (b.order || 0) - (a.order || 0));
    } else {
      // 在時間模式下，按創建時間倒序排列
      return [...articles].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
  }, [articles, sortMode]);

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">請先登入以管理文章。</Alert>
      </Container>
    );
  }

  // 檢查用戶是否有權限管理文章
  if (user.role !== 'ADMIN' && user.role !== 'COACH') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">只有教練和管理員可以管理文章。</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">文章管理</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            新增文章
          </Button>
          {sortMode === 'order' && user.role === 'ADMIN' && (
            <Button variant="outlined" startIcon={<SaveIcon />} onClick={saveOrder}>
              保存順序
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <TextField
          placeholder="搜尋標題或內容"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment: (
            <InputAdornment position="start"><SearchIcon /></InputAdornment>
          )}}
          sx={{ minWidth: 320 }}
        />
        
        <ToggleButtonGroup
          value={sortMode}
          exclusive
          onChange={(e, newMode) => newMode && setSortMode(newMode)}
          size="small"
        >
          <ToggleButton value="order">自定義排序</ToggleButton>
          <ToggleButton value="time">時間排序</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3}>
          {sortedArticles.map((article, index) => (
            <Grid item xs={12} md={6} lg={4} key={article.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: isDragging && draggedIndex === index ? 0.5 : 1,
                  cursor: (sortMode === 'order' && user.role === 'ADMIN') ? 'move' : 'default',
                  border: isDragging && draggedIndex === index ? '2px dashed #1976d2' : 'none'
                }}
                draggable={sortMode === 'order' && user.role === 'ADMIN'}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <CardContent>
                  {/* 添加圖片顯示 */}
                  {/*
                  {article.image && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <img
                        src={article.image}
                        alt={article.title}
                        style={{ 
                          width: '100%', 
                          height: '200px', 
                          objectFit: 'contain',
                          borderRadius: '4px'
                        }}
                      />
                    </Box>
                  )}
                  */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {sortMode === 'order' && (
                      <DragIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    )}
                    <Typography variant="h6" gutterBottom sx={{ flex: 1 }}>
                      {article.title}
                    </Typography>
                    <Chip 
                      label={article.is_published ? '已發布' : '草稿'} 
                      color={article.is_published ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {article.content.slice(0, 60)}{article.content.length > 160 ? '…' : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {(article.tag_list || []).map((t) => <Chip key={t} label={t} size="small" />)}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    作者：{article.author?.username} | 排序：{article.order || 0}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Tooltip title="編輯">
                    <span>
                      <IconButton color="primary" onClick={() => handleOpenDialog(article)} disabled={!isOwner(article)}>
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="刪除">
                    <span>
                      <IconButton color="error" onClick={() => handleDelete(article.id)} disabled={!isOwner(article)}>
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingArticle ? '編輯文章' : '新增文章'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField label="標題" fullWidth sx={{ mb: 2 }} value={formData.title} onChange={handleChange('title')} required />
            {/*
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>文章圖片</Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="image-upload">
                <Button variant="outlined" component="span" sx={{ mb: 1 }}>
                  選擇圖片
                </Button>
              </label>
              {previewImage && (
                <Box sx={{ mt: 1 }}>
                  <img
                    src={previewImage}
                    alt="預覽"
                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Box>
            */}
            <Box sx={{ mb: 2 }}>
              <SimpleRichTextEditor
                value={formData.content}
                onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                placeholder="輸入文章內容，支援圖片上傳..."
              />
            </Box>
            
            <TextField label="標籤" fullWidth sx={{ mb: 2 }} value={formData.tags} onChange={handleChange('tags')} placeholder="以逗號分隔：技巧, 裝備" />
            <FormControlLabel control={<Switch checked={formData.is_published} onChange={handleChange('is_published')} />} label="立即發布" />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>取消</Button>
            <Button type="submit" variant="contained">{editingArticle ? '更新' : '發布'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default ArticleManage;
